import { SYNC_OPERATION_STATUS, SYNC_OPERATION_TYPES } from './flowdeskDataModel.js';

const DEFAULT_MAX_QUEUE_SIZE = 500;

const createOperationId = ({ entity, entityId, type, createdAt }) => (
  [
    'sync',
    entity,
    entityId || 'bulk',
    type,
    createdAt,
    Math.random().toString(36).slice(2, 8)
  ]
    .map(part => String(part).replace(/[^a-zA-Z0-9_-]/g, '_'))
    .join('_')
);

export const normalizeSyncQueue = (queue) => (
  Array.isArray(queue)
    ? queue.filter(Boolean).map((operation) => ({
      id: operation.id || createOperationId({
        entity: operation.entity || 'unknown',
        entityId: operation.entityId,
        type: operation.type || SYNC_OPERATION_TYPES.upsert,
        createdAt: operation.createdAt || new Date().toISOString()
      }),
      entity: operation.entity || 'unknown',
      entityId: operation.entityId || null,
      type: Object.values(SYNC_OPERATION_TYPES).includes(operation.type)
        ? operation.type
        : SYNC_OPERATION_TYPES.upsert,
      payload: operation.payload ?? null,
      status: Object.values(SYNC_OPERATION_STATUS).includes(operation.status)
        ? operation.status
        : SYNC_OPERATION_STATUS.pending,
      attempts: Number(operation.attempts || 0),
      lastError: operation.lastError || null,
      createdAt: operation.createdAt || new Date().toISOString(),
      updatedAt: operation.updatedAt || operation.createdAt || new Date().toISOString()
    }))
    : []
);

export const createSyncOperation = ({
  entity,
  entityId = null,
  type = SYNC_OPERATION_TYPES.upsert,
  payload = null,
  status = SYNC_OPERATION_STATUS.pending,
  now = new Date()
}) => {
  const createdAt = now.toISOString();

  return {
    id: createOperationId({ entity, entityId, type, createdAt }),
    entity,
    entityId,
    type,
    payload,
    status,
    attempts: 0,
    lastError: null,
    createdAt,
    updatedAt: createdAt
  };
};

export const addSyncOperation = ({ queue, operation, maxQueueSize = DEFAULT_MAX_QUEUE_SIZE }) => {
  const normalizedQueue = normalizeSyncQueue(queue);
  const nextQueue = [...normalizedQueue, operation];

  return nextQueue.slice(Math.max(0, nextQueue.length - maxQueueSize));
};

export const markSyncOperationStatus = ({ queue, operationId, status, error = null, now = new Date() }) => (
  normalizeSyncQueue(queue).map((operation) => {
    if (operation.id !== operationId) return operation;

    return {
      ...operation,
      status,
      attempts: status === SYNC_OPERATION_STATUS.failed ? operation.attempts + 1 : operation.attempts,
      lastError: error,
      updatedAt: now.toISOString()
    };
  })
);

export const removeSyncedOperations = (queue) => (
  normalizeSyncQueue(queue).filter(operation => operation.status !== SYNC_OPERATION_STATUS.synced)
);

export const getPendingSyncOperations = (queue) => (
  normalizeSyncQueue(queue).filter(operation => (
    operation.status === SYNC_OPERATION_STATUS.pending
    || operation.status === SYNC_OPERATION_STATUS.syncing
    || (operation.status === SYNC_OPERATION_STATUS.failed && Number(operation.attempts || 0) < 5)
  ))
);
